import { t, tf } from "./i18n.js";
// ---------------------------------------------------------------------------
// Auto-Scoring: Schnittstelle zwischen Kamera-Tracker und Spiellogik
//
// Der Tracker laeuft als eigener Prozess auf demselben Rechner und meldet
// erkannte Wuerfe. Diese Datei nimmt sie entgegen, zeigt sie an und uebergibt
// die fertige Aufnahme an dieselbe Funktion, die auch der Ziffernblock nutzt:
// submit501Score(). Damit gibt es keinen zweiten Weg durch die Spiellogik -
// automatisch und von Hand gezaehlte Wuerfe landen im selben Code.
//
// Bestaetigt wird durch das ZIEHEN der Pfeile. Der Tracker merkt, wenn das
// Board leer ist, und meldet "cleared". Solange die Pfeile stecken, laesst
// sich jeder Wurf antippen und korrigieren. Das passt zum natuerlichen
// Ablauf am Board: Man schaut hin, sieht den Fehler, korrigiert, zieht.
//
// Die Verbindung selbst gibt es noch nicht. Damit die Oberflaeche trotzdem
// gebaut und geprueft werden kann, steckt unten ein Simulator drin:
//
//     autoScore.simulate()            eine zufaellige Aufnahme
//     autoScore.simulate([60, 60, 20])   eine bestimmte
// ---------------------------------------------------------------------------

const SLOTS = 3;

const state = {
  connection: "getrennt", // getrennt | verbindet | verbunden
  darts: [], // { points, label, source: "erkannt" | "korrigiert" }
  active: false, // Auto-Scoring als Eingabeart gewaehlt?
  tracker: null, // { version, model_hash, board_id } aus dem hello
  // Wertet DIESES Fenster? (Tracker-Protokoll: claim/claimed/released)
  //
  // Vorbelegt mit true, und das ist Absicht: Ein aelterer Tracker kennt
  // "claim" nicht und antwortet nie. Stuende hier false, wuerde die Seite
  // mit so einem Tracker gar nicht mehr werten - eine Verschlechterung
  // gegenueber vorher. Nur ein Tracker, der die Rolle ausdruecklich
  // ENTZIEHT, setzt sie auf false.
  wertend: true,
};

let socket = null;
let editingIndex = null;

// Pro-Dart-Wertung (13.08.2026): true, sobald die laufende Aufnahme durch
// Bust oder Finish VOR dem dritten Dart abgeschlossen wurde. Nachzuegler
// (der Spieler wirft den dritten Pfeil trotzdem) gehoeren zu einem Zug,
// der vorbei ist - sie werden bis zum naechsten "cleared" verworfen.
let aufnahmeGesperrt = false;
// Warum wurde vorzeitig abgeschlossen? "bust" | "finish" - steuert nur die
// Anzeige (roter Hinweis), nicht die Wertung.
let aufnahmeAbschlussGrund = null;

// ── Modus-Adapter (13.08.2026): Auto-Scoring fuer alle Spielmodi ───────────
//
// Bisher kannte diese Datei genau einen Abnehmer (window.submit501Score).
// Jetzt registriert jeder Spielmodus einen Adapter unter seinem
// currentAppMode-Schluessel:
//
//   window.autoScoreAdapter["secure"] = {
//     aktiv()                 -> laeuft der Modus gerade? (Screen sichtbar)
//     ziel()                  -> Element, VOR dem der Autoscore-Kasten
//                                stehen soll (er WANDERT dorthin -
//                                verschieben statt nachbauen, dasselbe
//                                Muster wie verlagereDetails in
//                                board-input.js; die IDs bleiben eindeutig)
//     dart(dart, summe, n)    -> optional, PRO Dart. Rueckgabe
//                                {abschliessen, bust} beendet die Aufnahme
//                                vorzeitig (501); Modi, die den Dart sofort
//                                verwerten (Secure, Cricket), geben nichts
//                                zurueck.
//     aufnahme(summe, wuerfe) -> optional, beim Ziehen der Pfeile. false =
//                                abgelehnt (Slots bleiben stehen). Fehlt
//                                die Funktion, gilt die Aufnahme als
//                                angenommen (Pro-Dart-Modi).
//     markiere(dart)          -> optional: Dart im Modus-SVG markieren
//                                (markiereUniversalDartboard in ui.js)
//     live(darts)             -> optional: aktuell erkannte, noch NICHT
//                                gewertete Darts als pulsierende Punkte
//                                zeigen ([] loescht sie). 501 zeichnet
//                                sie auf das Eingabe-Board
//                                (boardLiveDarts501 in board-input.js).
//     geleert()               -> optional: Board wurde geleert (Marken
//                                und Live-Punkte des Modus aufraeumen)
//     korrigierbar            -> true: Editor/Slots-Antippen erlaubt.
//                                Pro-Dart-Modi lassen das aus - ihr Dart
//                                ist beim Antippen laengst verwertet, eine
//                                Korrektur hier wuerde doppelt zaehlen.
//   }
//
// Die Wertungshoheit bleibt beim Modus; diese Datei nimmt nur entgegen,
// zeigt an und reicht weiter - wie bisher bei 501.
window.autoScoreAdapter = window.autoScoreAdapter || {};

/** Welcher Modus laeuft gerade?
 *
 *  BEFUND 14.08.2026: Diese Funktion hat frueher blind `currentAppMode`
 *  geglaubt - und der wurde nur von 501, Secure und Scoring gesetzt. In
 *  Bob's, RTW, 121, Gotcha, Random, Party und Cricket stand dort "", der
 *  Rueckfall `|| "501"` griff, der 501-Adapter meldete auf fremdem
 *  Bildschirm `aktiv() === false` - und der Tracker tat NICHTS. Sieben
 *  Modi, deren Adapter vorhanden und richtig waren, aber nie gefragt wurden.
 *
 *  `currentAppMode` wird jetzt ueberall gesetzt (openGameSetup). Damit ein
 *  vergessener Modus aber nie wieder still das Auto-Scoring abschaltet,
 *  fragt diese Funktion im Zweifel die Adapter selbst: Deren `aktiv()`
 *  prueft ohnehin den eigenen Bildschirm - das ist die belastbarere
 *  Auskunft als eine Variable, die jemand setzen muss. */
function aktiverModus() {
  const gemeldet = window.currentAppMode || "501";
  const adapter = window.autoScoreAdapter[gemeldet];
  if (adapter && (typeof adapter.aktiv !== "function" || adapter.aktiv())) {
    return gemeldet;
  }
  for (const [modus, kandidat] of Object.entries(window.autoScoreAdapter)) {
    if (typeof kandidat.aktiv === "function" && kandidat.aktiv()) return modus;
  }
  return null;
}

function aktiverAdapter() {
  const mode = aktiverModus();
  if (!mode) return null;
  const adapter = window.autoScoreAdapter[mode];
  if (!adapter) return null;
  // 501 behaelt seinen Eingabeart-Schalter (Ziffernblock/Board/Auto):
  // dort entscheidet weiterhin state.active. Die uebrigen Modi haben
  // keinen Schalter - bei ihnen genuegt ein laufendes Spiel.
  if (mode === "501" && !state.active) return null;
  return adapter;
}

// Den Autoscore-Kasten in den aktiven Modus verschieben. Er wohnt im
// 501-Partial; fuer andere Modi wandert das DOM-Element mitsamt seinen
// IDs an die Zielstelle des Adapters und zurueck.
function verlagereAutoscoreKasten(adapter) {
  const kasten = el("autoscore-container-501");
  if (!kasten || !adapter || typeof adapter.ziel !== "function") return;
  const ziel = adapter.ziel();
  if (!ziel || !ziel.parentElement) return;
  if (ziel.previousElementSibling !== kasten) {
    ziel.parentElement.insertBefore(kasten, ziel);
  }
}

// ── Anzeige ────────────────────────────────────────────────────────────────

function el(id) {
  return document.getElementById(id);
}

function labelFor(points, mult, base) {
  if (base === 25) return "BULL";
  if (base === 50) return "B-EYE";
  if (points === 0) return "MISS";
  if (mult === 2) return "D" + base;
  if (mult === 3) return "T" + base;
  return String(base);
}

// Sektoren im Uhrzeigersinn ab 20 (oben) - dieselbe Liste wie board.yaml
// und die Statusseite des Trackers.
const SEKTOREN_UHRZEIGER = [
  20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5,
];

// Miss MIT Position -> "M<Sektor>" (Alpha-Wunsch 13.08.2026): Nicht "M1,
// M2, M3" nach Slotnummer, sondern das FELD, neben dem der Pfeil steckt.
// Der Winkel zaehlt im Uhrzeigersinn von oben (x nach rechts, y nach oben,
// wie im Tracker-Protokoll); jeder Sektor ist 18 Grad breit, die Grenze
// liegt bei +-9 Grad um die Feldmitte.
function missLabel(xMm, yMm) {
  if (!Number.isFinite(xMm) || !Number.isFinite(yMm)) return "MISS";
  const winkelGrad = (Math.atan2(xMm, yMm) * 180) / Math.PI;
  const index = ((Math.round(winkelGrad / 18) % 20) + 20) % 20;
  return "M" + SEKTOREN_UHRZEIGER[index];
}

function render() {
  // Kasten in den aktiven Modus verschieben und dort zeigen. 501 steuert
  // die Sichtbarkeit weiter ueber seinen Eingabeart-Schalter (setActive);
  // die uebrigen Modi zeigen den Kasten, sobald ein Tracker verbunden ist.
  const modusAdapter = aktiverAdapter();
  if (modusAdapter) {
    verlagereAutoscoreKasten(modusAdapter);
    if ((window.currentAppMode || "501") !== "501") {
      const kasten = el("autoscore-container-501");
      if (kasten) {
        kasten.style.display =
          state.connection === "verbunden" ? "block" : "none";
      }
    }
  }

  // Solange der Tracker wertet, ist der Autoscore-Kasten die EINZIGE
  // Eingabe im 501 - game-501.js blendet Board und Ziffernblock aus und
  // holt sie zurueck, sobald der Tracker wegfaellt. Hier steht nur der
  // Anstoss; die Entscheidung faellt dort, wo die Eingabearten wohnen.
  //
  // Und umgekehrt: Wertet der Tracker NICHT (getrennt, oder ein anderes
  // Fenster hat die Wertung), schrumpft der Kasten auf seine Statuszeile.
  // Die drei Dart-Kacheln stehen sonst als tote Eingabe neben der
  // zurueckgekehrten Handeingabe - die Zwei-Felder-Frage waere nur
  // verschoben statt beantwortet. Die Statuszeile bleibt, weil sie den
  // Ausfall meldet und ein Antippen die Verbindung neu anstoesst.
  const kasten501 = el("autoscore-container-501");
  if (kasten501) {
    const modusAdapter2 = aktiverAdapter();
    if (modusAdapter2 && modusAdapter2.eigeneSlots) {
      // Der Modus bringt eigene Dart-Kacheln mit (aufnahme-eingabe.js).
      // Dann zeigt der Kasten NUR seine Statuszeile - zwei Reihen Darts
      // nebeneinander waeren genau die Doppeldeutigkeit, die im 501
      // abgestellt wurde.
      kasten501.dataset.eingabe = "ruhend";
    } else if ((window.currentAppMode || "501") === "501") {
      const wertetHier =
        state.active && state.wertend && state.connection === "verbunden";
      kasten501.dataset.eingabe = wertetHier ? "aktiv" : "ruhend";
    } else {
      // Secure/Cricket zeigen je Dart - dort ist der Kasten immer Anzeige.
      delete kasten501.dataset.eingabe;
    }
  }
  if (typeof window.aktualisiere501Eingabe === "function") {
    window.aktualisiere501Eingabe();
  }

  // Dasselbe fuer die Modi mit eigener Eingabe (aufnahme-eingabe.js).
  //
  // BEFUND 15.08.2026: Die Zeile darueber gab es nur fuer 501. Ein Bauteil
  // rechnete seine Sichtbarkeit deshalb NUR beim Spielstart aus. Ging der
  // Tracker mitten im Spiel an, blieb die Scheibe klickbar - die Regel
  // "bei laufendem Tracker ist die Scheibe nur noch Bild" galt ausserhalb
  // von 501 also nur, wenn der Tracker schon vor dem Start wertete.
  if (modusAdapter && typeof modusAdapter.sichtbarkeit === "function") {
    modusAdapter.sichtbarkeit();
  }

  // Wurfbilder: ein Bild je Dart-Slot, in einer Reihe unter dem Score.
  // Sichtbar genau solange der jeweilige Dart in der Aufnahme steht -
  // EINE Regel, ein Besitzer (diese Funktion). Ziehen, cleared,
  // hello-Resync und die Uebernahme leeren state.darts und nehmen die
  // Bilder damit von selbst mit. Die Reihe haengt im Autoscore-Kasten
  // und wandert mit ihm in den aktiven Modus (verlagereAutoscoreKasten).
  const wurfbildReihe = el("autoscore-wurfbilder");
  if (wurfbildReihe) {
    let eines = false;
    for (let i = 0; i < SLOTS; i++) {
      const bildEl = el("autoscore-wurfbild-" + i);
      if (!bildEl) continue;
      const daten = state.darts[i]?.bild || null;
      if (daten) {
        eines = true;
        if (bildEl.src !== daten) bildEl.src = daten;
        bildEl.style.visibility = "visible";
      } else {
        if (bildEl.src) bildEl.removeAttribute("src");
        // visibility statt display: Ein leerer Slot haelt seine Spalte,
        // damit Bild 2 unter Kachel 2 steht und nicht nach links rueckt.
        bildEl.style.visibility = "hidden";
      }
    }
    // Ohne ein einziges Bild faellt die ganze Reihe weg (kein Leerraum
    // bei aelterem Tracker oder ausgeschaltetem Wurfbild).
    wurfbildReihe.style.display = eines ? "flex" : "none";
  }

  const status = el("autoscore-status");
  if (status) {
    status.dataset.state = state.connection;
    const text = el("autoscore-status-text");
    if (text) {
      let anzeige = {
        verbunden: t("js.autoscore.010"),
        verbindet: t("js.autoscore.011"),
        getrennt: t("js.autoscore.008"),
      }[state.connection];
      // Verbunden, aber ein anderes Fenster wertet. Ohne diesen Hinweis
      // stuende hier "Tracker verbunden", waehrend nichts ankommt - der
      // verwirrendste denkbare Zustand.
      if (state.connection === "verbunden" && !state.wertend) {
        anzeige = t("js.autoscore.001");
      }
      // Version aus dem hello anzeigen - Grundlage fuer den
      // "Update verfuegbar"-Vergleich gegen latest.json (Konzept §2)
      if (state.connection === "verbunden" && state.tracker?.version) {
        anzeige += " · v" + state.tracker.version;
      }
      text.textContent = anzeige;
    }
  }

  for (let i = 0; i < SLOTS; i++) {
    const slot = el("autoscore-dart-" + i);
    if (!slot) continue;
    const dart = state.darts[i];
    if (!dart) {
      slot.dataset.state = "leer";
      slot.innerHTML = "<span>–</span>";
      delete slot.dataset.verdacht;
      slot.removeAttribute("title");
      continue;
    }
    slot.dataset.state = dart.source;
    // hand_verdacht (Anti-Cheat Stufe 2a): Der Tracker sah kurz vor der
    // Wertung eine Verdeckung oder armgrosse Unruhe. Markierung, kein
    // Urteil - die Kachel bekommt einen Rahmen und der Titel erklaert ihn;
    // das Wurfbild darunter ist der Beleg zum Selbst-Pruefen.
    if (dart.verdacht) {
      slot.dataset.verdacht = "1";
      slot.title = t("js.autoscore.014");
    } else {
      delete slot.dataset.verdacht;
      slot.removeAttribute("title");
    }
    // Fehlwuerfe durchnummeriert - siehe game-501.js. Diese Schleife zaehlt
    // ab 0, deshalb i + 1. Nur die Anzeige: dart.label bleibt "MISS", damit
    // Korrektur und Uebernahme unveraendert damit arbeiten koennen.
    slot.innerHTML =
      "<span>" +
      (dart.label === "MISS" ? "M" + (i + 1) : dart.label) +
      "</span><small>" +
      dart.points +
      (dart.source === "korrigiert" ? t("js.autoscore.012") : "") +
      "</small>";
  }

  const sum = state.darts.reduce((acc, d) => acc + d.points, 0);
  const sumEl = el("autoscore-sum-value");
  if (sumEl) sumEl.textContent = sum;

  const hint = el("autoscore-hint");
  if (hint) {
    hint.style.color = "";
    hint.style.fontWeight = "";
    if (aufnahmeGesperrt) {
      // Vorzeitiger Abschluss: Die Slots zeigen weiter, WOMIT die
      // Aufnahme endete - der Hinweis sagt rot bzw. gruen, WARUM.
      if (aufnahmeAbschlussGrund === "bust") {
        hint.textContent = t("js.autoscore.002");
        hint.style.color = "var(--accent-red)";
        hint.style.fontWeight = "bold";
      } else {
        hint.textContent = t("js.autoscore.013");
        hint.style.color = "var(--accent-green)";
        hint.style.fontWeight = "bold";
      }
    } else if (state.connection !== "verbunden") {
      hint.textContent = t("js.autoscore.003");
    } else if (state.darts.length === 0) {
      hint.textContent = t("js.autoscore.009");
    } else if (modusAdapter && !modusAdapter.korrigierbar) {
      // Pro-Dart-Modi: Der Dart ist sofort verwertet, der Editor ist zu.
      hint.textContent = t("js.autoscore.004");
    } else if (state.darts.length < SLOTS) {
      hint.textContent =
        t("js.autoscore.005");
    } else {
      hint.textContent = t("js.autoscore.006");
    }
  }
}

// ── Korrektur ──────────────────────────────────────────────────────────────

function openEditor(index) {
  const adapter = aktiverAdapter();
  // Korrigieren nur, wo der Modus es traegt: In Pro-Dart-Modi (Secure,
  // Cricket) ist der angetippte Dart laengst verwertet - eine Korrektur
  // hier wuerde doppelt zaehlen. Dort ist der Weg das Undo des Modus.
  if (!adapter || !adapter.korrigierbar) return;
  // Eine vorzeitig uebernommene Aufnahme (Bust/Finish) ist gewertet -
  // eine Korrektur hier wuerde ein zweites Mal einreichen. Der Weg fuer
  // eine falsche Wertung ist das Undo.
  if (aufnahmeGesperrt) return;
  editingIndex = index;

  const overlay = el("autoscore-editor");
  if (!overlay) return;
  overlay.style.display = "flex";

  const grid = el("autoscore-editor-grid");
  if (grid && !grid.dataset.built) {
    const buttons = [];
    for (let n = 1; n <= 20; n++) buttons.push({ base: n, mult: 1 });
    buttons.push({ base: 25, mult: 1 });
    buttons.push({ base: 50, mult: 1 });
    buttons.push({ base: 0, mult: 1 });
    grid.innerHTML = buttons
      .map(
        (b) =>
          '<button type="button" class="btn-glass" data-base="' +
          b.base +
          '">' +
          (b.base === 25 ? "BULL" : b.base === 50 ? "B-EYE" : b.base === 0 ? "MISS" : b.base) +
          "</button>"
      )
      .join("");
    grid.dataset.built = "1";
    grid.addEventListener("click", (event) => {
      const button = event.target.closest("button[data-base]");
      if (!button) return;
      applyCorrection(Number(button.dataset.base));
    });
  }
  setEditorMult(1);
}

let editorMult = 1;

function setEditorMult(mult) {
  editorMult = mult;
  for (const m of [1, 2, 3]) {
    const button = el("autoscore-mult-" + m);
    if (button) button.classList.toggle("active", m === mult);
  }
}

function applyCorrection(base) {
  // Bull, Bullseye und Miss haben keinen eigenen Multiplikator
  const mult = base === 25 || base === 50 || base === 0 ? 1 : editorMult;
  const points = base * mult;

  const dart = {
    points,
    // base/mult auch am korrigierten Dart (13.08.2026): Die Pro-Dart-
    // Wertung prueft beim Finish auf Doppel - eine Korrektur auf D-Feld
    // muss dabei genauso zaehlen wie ein erkanntes.
    base,
    mult,
    label: labelFor(points, mult, base),
    source: "korrigiert",
    // Das Wurfbild BLEIBT - anders als heat, das die Korrektur bewusst
    // verwirft: Die Koordinate ist eine Ableitung des Trackers und nach
    // einer Fehllesung verdaechtig, das Foto ist das rohe Kamerabild und
    // gerade nach einer Korrektur der Beleg, wo der Pfeil wirklich steckt.
    bild:
      editingIndex !== null && state.darts[editingIndex]
        ? state.darts[editingIndex].bild || null
        : null,
    // Auch der Verdacht ueberlebt die Korrektur: Er beschreibt den MOMENT
    // des Wurfs, nicht das erkannte Feld.
    verdacht:
      editingIndex !== null && state.darts[editingIndex]
        ? state.darts[editingIndex].verdacht === true
        : false,
  };

  let index;
  if (editingIndex === null || editingIndex >= state.darts.length) {
    state.darts.push(dart);
    index = state.darts.length - 1;
  } else {
    state.darts[editingIndex] = dart;
    index = editingIndex;
  }

  meldeKorrektur(index, base, mult, points);

  closeEditor();
  render();

  // Nach einer Korrektur die Pro-Dart-Wertung neu anstossen: Die Summe
  // hat sich geaendert - Live-Rest, Bust und Finish muessen nachziehen.
  bewerteLetztenDart();
}

/** Korrektur an den Tracker zurueckmelden.
 *
 *  Dort ist sie das wertvollste Trainingssignal (Hard Case - der Tracker
 *  sichert das Ausloese-Frame, Opt-in, siehe KONZEPT_TRACKER_EXE.md §3).
 *
 *  Eigene Funktion seit 15.08.2026: Der Editor im Autoscore-Kasten war
 *  bisher der einzige Weg, eine Korrektur zu melden - und der haengt nur an
 *  den drei Kacheln des 501. Die Modi mit eigener Eingabe korrigieren in
 *  ihrem Bauteil und brauchen denselben Rueckkanal; sonst faellt genau das
 *  Signal weg, wegen dem der Rueckkanal gebaut wurde. */
function meldeKorrektur(index, base, mult, points) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    try {
      socket.send(
        JSON.stringify({ type: "correction", index, base, mult, points })
      );
    } catch (error) {
      // Nur Telemetrie - eine kaputte Verbindung darf die Korrektur
      // in der Anzeige nicht verhindern
    }
  }
}

function closeEditor() {
  editingIndex = null;
  const overlay = el("autoscore-editor");
  if (overlay) overlay.style.display = "none";
}

// ── Ereignisse vom Tracker ─────────────────────────────────────────────────

function onThrow(payload) {
  // Doppelte Sicherung: Ein Tracker mit Anspruchsvergabe schickt hier
  // ohnehin nichts mehr her. Ein aelterer koennte es - dann faengt es
  // diese Zeile ab, sobald wir wissen, dass ein anderes Fenster wertet.
  if (!state.wertend) return;
  const adapter = aktiverAdapter();
  if (!adapter) return;
  if (aufnahmeGesperrt) return;
  if (state.darts.length >= SLOTS) return;

  const base = Number(payload.base ?? 0);
  const mult = Number(payload.mult ?? 1);
  const points = Number(payload.points ?? base * mult);

  // Wurfbild (20.08.2026, je Dart seit dem Alpha-Wunsch vom selben Tag):
  // Ausschnitt um die Spitze, vom Tracker als data-URL mitgeschickt
  // (additives Feld, aeltere Tracker haben es nicht). Das Bild haengt am
  // Dart selbst - drei Slots, drei Bilder - und verschwindet damit von
  // selbst, wenn die Darts verschwinden. Nur data:image/-URLs werden
  // uebernommen: der Socket ist lokal, aber ein <img src> ist kein Ort
  // fuer Ueberraschungen.
  const bild =
    typeof payload.bild === "string" && payload.bild.startsWith("data:image/")
      ? payload.bild
      : null;

  state.darts.push({
    bild,
    // Anti-Cheat Stufe 2a: Markierung des Trackers, kein Urteil.
    verdacht: payload.hand_verdacht === true,
    points,
    // base/mult bleiben am Dart (13.08.2026): Die Pro-Dart-Wertung braucht
    // sie fuer die Doppel-Pruefung beim Finish - aus points allein ist
    // "D20" nicht von "S20+S20" zu unterscheiden.
    base,
    mult,
    label:
      points === 0 && payload.x_mm !== undefined
        ? missLabel(Number(payload.x_mm), Number(payload.y_mm))
        : payload.label || labelFor(points, mult, base),
    source: "erkannt",
    // Heatmap (KONZEPT_HEATMAP.md P1): Boardkoordinaten aus der Fusion,
    // falls der Tracker sie mitschickt. Korrigierte Wuerfe verlieren die
    // Koordinate bewusst - hat der Tracker das Feld falsch gelesen, ist
    // auch die Position verdaechtig.
    heat:
      payload.x_mm !== undefined && payload.y_mm !== undefined
        ? {
            base,
            mult,
            x_mm: Number(payload.x_mm),
            y_mm: Number(payload.y_mm),
            confidence:
              payload.confidence !== undefined
                ? Number(payload.confidence)
                : null,
          }
        : null,
  });
  render();

  // Dart im Modus-SVG markieren (falls der Modus ein Board zeigt und der
  // Tracker Koordinaten mitgeschickt hat)
  if (typeof adapter.markiere === "function") {
    try {
      adapter.markiere(state.darts[state.darts.length - 1]);
    } catch (error) {
      // Anzeige darf die Wertung nie stoppen
    }
  }

  // ── Pro-Dart-Wertung (13.08.2026, Alpha-Wunschliste) ──────────────────
  // Der Spielmodus bekommt jeden erkannten Dart sofort gemeldet. Bei 501
  // antwortet er, ob die Aufnahme VORZEITIG endet (Bust, Finish); Modi
  // wie Secure und Cricket verwerten den Dart direkt und geben nichts
  // zurueck. Die WERTUNG bleibt in jedem Fall beim Modus.
  bewerteLetztenDart(adapter);
}

// ── Live-Erkennung (13.08.2026 abends) ─────────────────────────────────────
//
// Der Tracker meldet mit {"type":"live","darts":[...]} die Darts, die die
// Fusion GERADE sieht, die aber noch nicht gezaehlt sind (Stabilitaetstor
// laeuft). Sie erscheinen als pulsierender Punkt auf der digitalen
// Scheibe des aktiven Modus und verschwinden, sobald der Tracker den Dart
// endgueltig zaehlt (naechste live-Nachricht ohne ihn; der bestaetigte
// Wurf kommt als normales throw). Reine Anzeige: Koordinaten sind
// dieselben Board-Millimeter wie im throw - keine Umrechnung, keine
// Wertung, kein zusaetzlicher Zustand.
function onLive(message) {
  // Nur das wertende Fenster zeigt Live-Punkte - im anderen Fenster
  // stuende ein Puls ohne die dazugehoerigen Slots.
  if (!state.wertend) return;
  const adapter = aktiverAdapter();
  if (!adapter || typeof adapter.live !== "function") return;
  try {
    adapter.live(Array.isArray(message.darts) ? message.darts : []);
  } catch (error) {
    // Anzeige darf die Wertung nie stoppen
  }
}

/** Live-Punkte im aktiven Modus loeschen (cleared, hello, Spielwechsel). */
function leereLiveAnzeige() {
  const adapter = aktiverAdapter();
  if (adapter && typeof adapter.live === "function") {
    try {
      adapter.live([]);
    } catch (error) {
      // Anzeige darf die Wertung nie stoppen
    }
  }
}

async function bewerteLetztenDart(adapter) {
  adapter = adapter || aktiverAdapter();
  if (!adapter || typeof adapter.dart !== "function") return;
  if (state.darts.length === 0) return;

  const sum = state.darts.reduce((acc, d) => acc + d.points, 0);
  const letzter = state.darts[state.darts.length - 1];
  const urteil = adapter.dart(letzter, sum, state.darts.length);
  if (!urteil || !urteil.abschliessen) return;

  await uebernehmeAufnahme({ erzwungenerBust: !!urteil.bust, vorzeitig: true });
}

// Gemeinsamer Abschluss fuer "Pfeile gezogen" und den vorzeitigen
// Pro-Dart-Abschluss (Bust/Finish): erst entscheidet das Spiel, dann
// erst werden Slots geleert und die Heatmap beliefert.
async function uebernehmeAufnahme(optionen = {}) {
  if (state.darts.length === 0) return;
  const adapter = aktiverAdapter();
  if (!adapter) {
    // Kein Modus, der die Aufnahme nehmen koennte - wegwerfen ist hier
    // richtig, sonst steht sie beim naechsten Spiel wieder da.
    state.darts = [];
    render();
    return;
  }

  const aufnahme = state.darts.slice();
  const sum = aufnahme.reduce((acc, d) => acc + d.points, 0);

  // Modi mit aufnahme() entscheiden selbst (false = abgelehnt, z. B.
  // Gegner am Zug). Pro-Dart-Modi ohne aufnahme() haben jeden Dart
  // laengst verwertet - fuer sie ist das Ziehen nur noch Aufraeumen.
  let angenommen = true;
  if (typeof adapter.aufnahme === "function") {
    angenommen = await adapter.aufnahme(sum, aufnahme, {
      erzwungenerBust: !!optionen.erzwungenerBust,
    });
  }
  if (angenommen === false) {
    render();
    return;
  }

  // Heatmap: nur erkannte Wuerfe MIT Koordinaten (Batch je Aufnahme).
  queueThrowRows(
    aufnahme.filter((d) => d.source === "erkannt" && d.heat).map((d) => d.heat),
  );

  // Vorzeitiger Abschluss (Bust/Finish vor dem Ziehen): Die Pfeile stecken
  // noch im Board - alles, was bis zum "cleared" noch gemeldet wird,
  // gehoert zum beendeten Zug und wird verworfen. Die SLOTS bleiben dabei
  // sichtbar (Alpha-Wunsch 13.08.2026: der Bust kam zu abrupt - man soll
  // sehen, WELCHE Darts die Aufnahme beendet haben); geleert wird erst
  // mit dem Ziehen. Beim Abschluss durch "cleared" ist das Board schon
  // leer - da wird sofort geleert und NICHT gesperrt.
  if (optionen.vorzeitig) {
    aufnahmeGesperrt = true;
    aufnahmeAbschlussGrund = optionen.erzwungenerBust ? "bust" : "finish";
  } else {
    state.darts = [];
  }
  render();
  flushThrowQueue();
}

async function onBoardCleared() {
  // BEFUND 20.08.2026: Hier stand `if (!aktiverAdapter()) return;` - damit
  // war der Wegwerf-Pfad in uebernehmeAufnahme ("kein Modus, der die
  // Aufnahme nehmen koennte") UNERREICHBAR: Ohne aktiven Bildschirm kam
  // das cleared nie dort an, aufgelaufene Darts blieben stehen und standen
  // beim naechsten Spiel wieder da. Genau das prueft
  // test_autoscore_verbindung.mjs (Abschnitt 8, letzter Fall). Ohne
  // Adapter wird jetzt trotzdem aufgeraeumt; die Wertung selbst laeuft
  // unveraendert nur ueber den Adapter.

  // Board ist leer - kein Dart kann mehr "live" sein.
  leereLiveAnzeige();

  // Dem Modus das leere Board melden (z. B. Board-Marken loeschen)
  const adapter = aktiverAdapter();
  if (adapter && typeof adapter.geleert === "function") {
    try {
      adapter.geleert();
    } catch (error) {
      // Anzeige darf die Wertung nie stoppen
    }
  }

  // Pro-Dart-Abschluss (13.08.2026): Die Aufnahme wurde schon bei Bust/
  // Finish uebernommen; das Ziehen der Pfeile beendet nur noch die Sperre
  // fuer Nachzuegler. Eventuell doch aufgelaufene Darts sind Reste des
  // beendeten Zugs - weg damit.
  if (aufnahmeGesperrt) {
    aufnahmeGesperrt = false;
    aufnahmeAbschlussGrund = null;
    state.darts = [];
    render();
    return;
  }

  if (state.darts.length === 0) return;

  // REIHENFOLGE, Befund Alphatest 09.08.2026 (gilt unveraendert, lebt
  // jetzt in uebernehmeAufnahme): Erst entscheidet das Spiel, dann erst
  // werden die Slots geleert. Eine abgelehnte Aufnahme (Gegner am Zug)
  // bleibt stehen und kann nach dem Zugwechsel bestaetigt werden.
  await uebernehmeAufnahme({ vorzeitig: false });
}

// ── Heatmap-Ingest (KONZEPT_HEATMAP.md, Stufe P1) ──────────────────────────
//
// Jede Aufnahme wird als Batch in die Supabase-Tabelle dart_throws
// geschrieben (Schema: supabase/dart_throws.sql). Schlaegt der Insert fehl
// (offline, Netzwerkfehler), bleibt der Batch in einer localStorage-
// Warteschlange und wird beim naechsten Erfolgsfall nachgereicht - ein
// still verlorener Wurf wuerde sonst die Verteilung verfaelschen.

const THROW_QUEUE_KEY = "secure-darts:throw-queue";
const THROW_QUEUE_MAX = 500; // Notbremse gegen unbegrenztes Wachstum

function readThrowQueue() {
  try {
    const raw = localStorage.getItem(THROW_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function writeThrowQueue(rows) {
  try {
    localStorage.setItem(THROW_QUEUE_KEY, JSON.stringify(rows.slice(-THROW_QUEUE_MAX)));
  } catch (error) {
    // Voller/gesperrter Speicher - dann gehen aeltere Wuerfe verloren
  }
}

function queueThrowRows(heatEntries) {
  if (!heatEntries || heatEntries.length === 0) return;

  const mode = window.currentAppMode || "501";
  const target =
    mode === "secure" && Array.isArray(window.targets)
      ? (window.targets[window.globalTargetIndex] ?? null)
      : null;

  const rows = heatEntries.map((h) => ({
    mode,
    target,
    base: h.base,
    mult: h.mult,
    x_mm: h.x_mm,
    y_mm: h.y_mm,
    confidence: h.confidence,
    is_training: !!window.isTrainingMode,
  }));

  writeThrowQueue(readThrowQueue().concat(rows));
}

let throwFlushRunning = false;

async function flushThrowQueue() {
  // Ohne Login keine Speicherung (RLS verlangt auth.uid()) - die Wuerfe
  // warten in der Queue, bis sich der Spieler anmeldet.
  if (window.isGuest || !window.currentUser || !window._supabase) return;
  if (throwFlushRunning) return;

  const rows = readThrowQueue();
  if (rows.length === 0) return;

  throwFlushRunning = true;
  try {
    const payload = rows.map((r) => ({ ...r, user_id: window.currentUser.id }));
    const { error } = await window._supabase.from("dart_throws").insert(payload);
    if (!error) writeThrowQueue([]);
    // Bei Fehler bleibt die Queue stehen - naechster Versuch beim
    // naechsten "cleared" oder Login (app.js ruft flushThrowQueue auf).
  } catch (error) {
    // Netzwerkfehler: Queue behalten
  } finally {
    throwFlushRunning = false;
  }
}

function setConnection(value) {
  state.connection = value;
  render();
}

/** Laufende Aufnahme verwerfen, Auto-Scoring aber anlassen.
 *
 * Gedacht fuer den Spielwechsel (app.js verlasseSpielzustand): Die drei
 * Pfeile des vorigen Spiels duerfen nicht ins naechste wandern. Der
 * Unterschied zu setActive(false) ist wichtig - das schaltete auch ab, und
 * eingeschaltet wird nur durch ein neues hello vom Tracker. */
function reset() {
  state.darts = [];
  // Auch die Nachzuegler-Sperre der Pro-Dart-Wertung aufheben: Nach einem
  // Spielwechsel direkt nach Bust/Finish (Pfeile stecken noch) bliebe sie
  // sonst bis zum naechsten "cleared" stehen und wuerfe des NEUEN Spiels
  // wuerden verworfen.
  aufnahmeGesperrt = false;
  aufnahmeAbschlussGrund = null;
  leereLiveAnzeige();
  closeEditor();
  render();
}

// ── Eingabeart umschalten ──────────────────────────────────────────────────

function setActive(value) {
  state.active = Boolean(value);
  const container = el("autoscore-container-501");
  if (container) container.style.display = state.active ? "block" : "none";
  if (state.active) {
    // Der Kasten kann in einem anderen Modus geparkt sein (Adapter-
    // Registry, 13.08.2026) - beim Einschalten der 501-Eingabeart holt
    // ihn diese Zeile an seinen Stammplatz zurueck.
    const heim = window.autoScoreAdapter && window.autoScoreAdapter["501"];
    if (heim) verlagereAutoscoreKasten(heim);
  } else {
    state.darts = [];
    closeEditor();
  }
  render();
}

// ── Verbindung zum lokalen Tracker ─────────────────────────────────────────
//
// Der Tracker ist ein Dienst auf DEM RECHNER DES SPIELERS (ws://127.0.0.1),
// diese Seite laeuft ueber HTTPS von secure-darts.com. Browser erlauben das,
// weil Loopback als vertrauenswuerdig gilt - siehe Hinweis unten.
//
// UMGEBAUT 08.08.2026. Vorher hatte connect() im gesamten Frontend keinen
// einzigen Aufrufer: Man musste die Browser-Konsole oeffnen und die Funktion
// von Hand tippen. Jetzt verbindet sich die Seite selbst, sobald ein Tracker
// laeuft, und schaltet das Auto-Scoring dann ein. Wer keinen Tracker hat,
// merkt davon nichts - der Versuch scheitert lautlos.

const TRACKER_URL = "ws://127.0.0.1:8770/live";

// Protokollfassung, die dieses Frontend versteht (tracker/protokoll.py:
// PROTOKOLL_VERSION). Der Tracker SENDET sie im hello - bisher hat sie
// niemand gelesen. Damit koennen kuenftige Feldaenderungen auffallen,
// statt still falsch interpretiert zu werden.
const PROTOKOLL_ERWARTET = 1;

// Wiederverbindung mit wachsendem Abstand. Ohne sie war ein Trackerneustart
// mitten im Spiel endgueltig: onclose setzte nur den Status und das war es.
const WARTEZEITEN_MS = [1000, 2000, 4000, 8000, 15000, 30000];
// Solange noch nie ein Tracker geantwortet hat, wird nur ein paar Mal
// versucht - die meisten Besucher der Seite haben keinen und sollen keine
// Dauerschleife im Hintergrund laufen lassen.
const VERSUCHE_OHNE_TRACKER = 3;

// `socket` ist bereits oben im Modulkopf deklariert.
let wiederholTimer = null;
let wiederholIndex = 0;
let autoVerbinden = true;
let trackerJeGesehen = false;

function planeWiederverbindung() {
  if (!autoVerbinden) return;
  if (!trackerJeGesehen && wiederholIndex >= VERSUCHE_OHNE_TRACKER) return;
  const wartezeit =
    WARTEZEITEN_MS[Math.min(wiederholIndex, WARTEZEITEN_MS.length - 1)];
  wiederholIndex += 1;
  clearTimeout(wiederholTimer);
  wiederholTimer = setTimeout(() => connect(TRACKER_URL, { auto: true }), wartezeit);
}

function connect(url, optionen = {}) {
  // Ein Aufruf von Hand (Konsole, Einstellungen) zaehlt als Wunsch des
  // Nutzers und schaltet die Selbstverbindung wieder ein.
  if (!optionen.auto) {
    autoVerbinden = true;
    wiederholIndex = 0;
  }
  clearTimeout(wiederholTimer);
  schliesseSocket();
  setConnection("verbindet");
  try {
    socket = new WebSocket(url || TRACKER_URL);
  } catch (error) {
    setConnection("getrennt");
    planeWiederverbindung();
    return;
  }
  const meiner = socket;

  socket.onopen = () => setConnection("verbunden");

  socket.onclose = () => {
    if (socket !== meiner) return; // veralteter Socket, ignorieren
    socket = null;
    setConnection("getrennt");
    planeWiederverbindung();
  };

  socket.onerror = () => {
    // onerror kommt IMMER zusammen mit onclose - die Wiederverbindung
    // wird deshalb nur dort geplant, sonst liefe sie doppelt.
    setConnection("getrennt");
  };

  socket.onmessage = (event) => {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch (error) {
      return;
    }
    if (message.type === "throw") onThrow(message);
    else if (message.type === "cleared") onBoardCleared();
    else if (message.type === "hello") onHello(message);
    else if (message.type === "claimed") uebernimmWertung();
    else if (message.type === "released") gibWertungAb(message.grund);
    else if (message.type === "live") onLive(message);
    // throw_info/cleared_info (Kopien fuer Nicht-Wertende) werden hier
    // bewusst ignoriert: Dieses Fenster wertet entweder selbst (dann
    // kommen throw/cleared) oder es zeigt "laeuft in einem anderen
    // Fenster" - eine zweite Anzeige waere nur verwirrend.
  };
}

function onHello(message) {
  const protokoll = Number(message.protocol || 1);
  if (protokoll !== PROTOKOLL_ERWARTET) {
    // Lieber gar nicht werten als falsch werten: Eine geaenderte
    // Feldbedeutung faellt sonst erst am falschen Punktestand auf.
    setConnection("getrennt");
    autoVerbinden = false;
    schliesseSocket();
    console.warn(
      `[autoscore] Tracker spricht Protokoll ${protokoll}, diese Seite ` +
        tf("js.autoscore.007", { p1: PROTOKOLL_ERWARTET }),
    );
    return;
  }

  trackerJeGesehen = true;
  wiederholIndex = 0;
  state.tracker = {
    version: String(message.version || ""),
    model_hash: String(message.model_hash || ""),
    // Kennung des AUFBAUS (Board + Kameras + Kalibrierung), nicht des
    // Rechners und nicht der Person. Leer, solange nicht kalibriert
    // wurde. Wird heute nur mitgefuehrt und angezeigt - die Verknuepfung
    // mit dem Konto ist TODO C2 Teil 2.
    board_id: String(message.board_id || ""),
  };

  // RESYNC: Ein hello heisst "der Tracker faengt von vorne an". Alles, was
  // die App noch als steckende Pfeile fuehrt, gehoert zu einer Aufnahme,
  // die es nicht mehr gibt. Ohne dieses Zuruecksetzen zaehlte ein
  // Trackerneustart bei zwei steckenden Pfeilen einen davon doppelt und
  // verwarf den vierten stumm (SLOTS-Deckel).
  state.darts = [];
  aufnahmeGesperrt = false;
  aufnahmeAbschlussGrund = null;
  closeEditor();
  leereLiveAnzeige();
  // Ein hello ist ein Neuanfang, also auch fuer die Rolle. Ohne diese
  // Zeile bliebe ein Fenster, dem einmal die Wertung entzogen wurde,
  // nach einem Trackerneustart dauerhaft stumm - und mit einem AELTEREN
  // Tracker, der "claim" gar nicht beantwortet, fuer immer.
  // Gefahrlos: Wer wirklich wertet, entscheidet der Tracker, nicht diese
  // Zeile. Bei zwei Fenstern bekommt der Verlierer sofort ein released.
  state.wertend = true;

  // Ein antwortender Tracker ist die Zusage, dass Auto-Scoring moeglich
  // ist - also einschalten. Das ist der Ersatz fuer die UI-Umschaltung,
  // die es noch nicht gibt.
  setActive(true);
  // Anspruch auf die Wertung anmelden. Ein aelterer Tracker ignoriert
  // die Nachricht, dann bleibt es beim Rundruf wie bisher.
  melde_anspruch();
  render();

  // Erst jetzt ist die laufende Trackerversion bekannt. app.js haengt daran
  // den Downloadhinweis ("Update verfuegbar" statt "jetzt herunterladen").
  // Ein Event statt eines direkten Aufrufs, damit autoscore.js nichts ueber
  // app.js wissen muss - die beiden Dateien haben sonst keine Beziehung.
  try {
    window.dispatchEvent(
      new CustomEvent("secure-darts:tracker-hello", {
        detail: { version: state.tracker.version },
      }),
    );
  } catch (error) {
    /* CustomEvent fehlt nur in sehr alten Umgebungen - dann eben ohne Hinweis */
  }
}

// ── Wer wertet (Tracker-Protokoll, TODO C2 Teil 3) ─────────────────────────
//
// Der Tracker schickt throw/cleared an ALLE verbundenen Clients. Zwei
// offene Tabs werteten damit jede Aufnahme doppelt - im lokalen 501 ohne
// jede Gegenwehr, weil dort keine Zugsperre greift. Aus 60 Punkten wurden
// 120, und es sah aus wie ein Erkennungsfehler.
//
// Geloest wird das im Tracker: Genau ein Client haelt die Rolle
// "scoring", der neueste Anspruch gewinnt. Diese Seite meldet ihn an und
// sagt es an, wenn sie ihn verliert.

function melde_anspruch() {
  if (!socket || socket.readyState !== 1) return;
  try {
    socket.send(JSON.stringify({ type: "claim", role: "scoring" }));
  } catch (error) {
    /* Verbindung gerade weg - der naechste hello meldet erneut an */
  }
}

function uebernimmWertung() {
  state.wertend = true;
  render();
}

function gibWertungAb(grund) {
  state.wertend = false;
  leereLiveAnzeige();
  // Die laufende Aufnahme gehoert dem anderen Fenster. Sie hier stehen
  // zu lassen, waere die Vorlage fuer genau die Doppelwertung, gegen die
  // das Ganze gebaut ist.
  state.darts = [];
  closeEditor();
  if (grund) console.info(`[autoscore] ${grund}`);
  render();
}

function schliesseSocket() {
  if (socket) {
    const alt = socket;
    socket = null;
    alt.onclose = null;
    alt.onerror = null;
    alt.onmessage = null;
    try {
      alt.close();
    } catch (error) {
      /* schon zu */
    }
  }
}

// ── Erneuter Anlauf, wenn der Tracker spaeter startet ──────────────────────
//
// BEFUND ALPHATEST 09.08.2026. VERSUCHE_OHNE_TRACKER=3 gibt rund acht
// Sekunden nach dem Laden der Seite endgueltig auf, solange nie ein
// Tracker geantwortet hat. Der uebliche Ablauf ist aber genau andersherum:
// Seite oeffnen, dann den Tracker starten. Danach kam nie wieder ein
// Versuch - Auto-Scoring blieb aus, ohne dass irgendwo etwas stand.
//
// Eine Dauerschleife ist trotzdem falsch: Die allermeisten Besucher haben
// keinen Tracker, und jeder Fehlversuch schreibt eine rote Zeile in ihre
// Konsole. Angestossen wird deshalb an den Stellen, an denen ein neuer
// Tracker WAHRSCHEINLICH ist: Tab wieder sichtbar, Fenster wieder im
// Fokus (genau das passiert beim Umschalten vom Trackerfenster zurueck),
// Netz wieder da - und auf ausdruecklichen Klick auf die Statuszeile.
const ANSTOSS_ABSTAND_MS = 15000;
let letzterAnstoss = 0;

function stosseVerbindungAn(sofort = false) {
  if (!autoVerbinden) return;
  if (socket) {
    // Verbunden, aber ein anderes Fenster wertet. Genau jetzt schaut der
    // Nutzer HIERHER - also die Rolle zurueckholen. Ein Ping-Pong kann
    // daraus nicht werden: Es ist immer nur ein Fenster im Fokus.
    if (!state.wertend) melde_anspruch();
    return; // laeuft schon oder verbindet gerade
  }
  const jetzt = Date.now();
  if (!sofort && jetzt - letzterAnstoss < ANSTOSS_ABSTAND_MS) return;
  letzterAnstoss = jetzt;
  wiederholIndex = 0;
  connect(TRACKER_URL, { auto: true });
}

function disconnect() {
  // Ausdrueckliches Trennen beendet auch die Selbstverbindung - sonst
  // waere der Knopf wirkungslos.
  autoVerbinden = false;
  clearTimeout(wiederholTimer);
  schliesseSocket();
  setConnection("getrennt");
}

// Beim Laden einmal probieren. Scheitert lautlos, wenn kein Tracker laeuft.
if (typeof window !== "undefined") {
  setTimeout(() => connect(TRACKER_URL, { auto: true }), 800);

  // Nach dem Aufwachen aus dem Ruhezustand oder Netzwechsel neu versuchen.
  window.addEventListener("online", () => stosseVerbindungAn());

  // Der wichtigste Fall: Der Nutzer startet den Tracker und wechselt
  // zurueck zum Browser. Genau dann feuert focus bzw. visibilitychange.
  window.addEventListener("focus", () => stosseVerbindungAn());
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) stosseVerbindungAn();
  });

  // Klick auf die Statuszeile = ausdruecklicher Wunsch, also ohne
  // Wartezeit. Ueber Delegation, damit die Zeile erst spaeter im DOM
  // stehen darf (sie kommt aus partials/501-game.html).
  document.addEventListener("click", (ereignis) => {
    const ziel = ereignis.target;
    if (ziel && ziel.closest && ziel.closest("#autoscore-status")) {
      stosseVerbindungAn(true);
    }
  });
}

// ── Simulator ──────────────────────────────────────────────────────────────

function simulate(scores) {
  setConnection("verbunden");
  const werte =
    scores ||
    Array.from({ length: SLOTS }, () => {
      const base = 1 + Math.floor(Math.random() * 20);
      const mult = [1, 1, 1, 2, 3][Math.floor(Math.random() * 5)];
      return base * mult;
    });

  werte.forEach((points, index) => {
    setTimeout(() => {
      const mult = points > 20 && points % 3 === 0 ? 3 : points > 20 ? 2 : 1;
      const base = points === 0 ? 0 : Math.round(points / mult);
      // Plausible Boardkoordinate fuer den Heatmap-Ingest mitsimulieren
      const coords = simulateCoords(base, mult);
      onThrow({ base, mult, points, ...coords, confidence: 0.9 });
    }, 400 * (index + 1));
  });

  setTimeout(() => onBoardCleared(), 400 * (werte.length + 1) + 1200);
}

// Feldmitte des getroffenen Segments plus Streuung - reicht, um den
// kompletten Ingest-Pfad ohne echten Tracker zu testen.
function simulateCoords(base, mult) {
  if (base === 0) {
    const winkel = Math.random() * 2 * Math.PI;
    return { x_mm: Math.cos(winkel) * 200, y_mm: Math.sin(winkel) * 200 };
  }
  if (base === 50) return { x_mm: Math.random() * 4 - 2, y_mm: Math.random() * 4 - 2 };
  if (base === 25) {
    const winkel = Math.random() * 2 * Math.PI;
    return { x_mm: Math.cos(winkel) * 10, y_mm: Math.sin(winkel) * 10 };
  }
  const order = window.UNIVERSAL_BOARD_ORDER || [];
  const idx = order.indexOf(base);
  // 20 sitzt oben; je Segment 18 Grad
  const winkel = ((idx >= 0 ? idx : 0) * 18 - 90) * (Math.PI / 180);
  const radius = mult === 3 ? 103 : mult === 2 ? 166 : 60;
  const rauschen = () => (Math.random() - 0.5) * 8;
  return {
    x_mm: Math.cos(winkel) * radius + rauschen(),
    y_mm: Math.sin(winkel) * radius + rauschen(),
  };
}

const autoScore = {
  connect,
  disconnect,
  sucheTracker: () => stosseVerbindungAn(true),
  reset,
  setActive,
  setConnection,
  // Beim Betreten eines Spielmodus aufrufen (app.js, enterMode): schiebt
  // den Autoscore-Kasten sofort in den neuen Modus, statt erst mit dem
  // ersten Wurfereignis.
  render,
  onThrow,
  onBoardCleared,
  openEditor,
  closeEditor,
  setEditorMult,
  meldeKorrektur,
  simulate,
  flushThrowQueue,
  get state() {
    return state;
  },
};

Object.assign(window, { autoScore, autoScoreRender: render, flushThrowQueue });

export default autoScore;
